import React, { useRef, useState, useCallback } from "react"
import { Image } from "@chakra-ui/react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { BadgeModel } from "../../../recoil/atoms/badges/types"
import { brandingSelector, roomSelector } from "../../../recoil/atoms/room"
import { usePictureInPicture } from "../../../hooks/usePictureInPicture"
import MouseHoverButtons from "../../common/mouseHoverButtons"
import { SDKContext } from "../../../context/SDKContext"
import {
  Container,
  Wrapper,
  ImageContainer,
  InfoContainer,
  Title,
  Description,
  Stack,
  PrimaryNumber,
  SecondaryNumber
} from "./badge.styled"
import { pipParamsSelector } from "../../../recoil/atoms/pictureInPicture"
import { productOverlaySelector } from "../../../recoil/atoms/product-overlay"
import { userListSelector } from "../../../recoil/atoms/users"
import { watchPartyScreenSelector } from "../../../recoil/atoms/watchPartyScreen"
import BuyButton from "./components/buy-button"
import { cleanProductDescription, stripHtmlTags } from "../../../util"
import { StylesWithSelectors } from "../../../types"
import useProductPageOverlay from "../../../hooks/useProductPageOverlay"
import { PRODUCT_DESTINATION } from "../../../constants/enums"

export interface BadgeProps {
  badgeHistoryData?: BadgeModel
  isModerator?: boolean
  itemId?: string
  badgeId?: string
  customStyles?: {
    button?: StylesWithSelectors
    container?: StylesWithSelectors
  }
}

/**
 * Shown in feature tab as history and tagged product
 */
const HistoryBadge = React.memo(
  ({ badgeHistoryData, isModerator, customStyles }: BadgeProps) => {
    const meetingClient = React.useContext(SDKContext)
    const badgeRef = useRef<HTMLDivElement>(null)
    const [hovered, setHovered] = useState(false)
    const roomStyles = useRecoilValue(brandingSelector)
    const isWatchParty = useRecoilValue(watchPartyScreenSelector)
    const allUserList = useRecoilValue(userListSelector)

    const {
      id,
      title,
      currentPrice,
      oldPrice,
      image,
      buttonText,
      checkoutUrl,
      channel,
      coverImages,
      shortDescription,
      description
    } = badgeHistoryData as BadgeModel

    const isShopifyProduct = channel === "shopify"

    const mainCameraEnabled = React.useMemo(
      () => allUserList.some(user => user.camEnabled),
      [allUserList]
    )

    const {
      openExternalUrl,
      requestPictureInPictureWP,
      requestPictureInPicture
    } = usePictureInPicture()

    const setPiPParams = useSetRecoilState(pipParamsSelector)
    const setProductOverlay = useSetRecoilState(productOverlaySelector)

    const coverImage = React.useMemo(
      () =>
        badgeHistoryData?.channel === "shopify"
          ? coverImages?.[0]?.src
          : coverImages?.[0],
      [coverImages, badgeHistoryData?.channel]
    )

    const selectedVariant = React.useMemo(() => {
      const variants = badgeHistoryData?.variants ?? []
      return variants ? variants[0] : {}
    }, [badgeHistoryData?.variants])

    const selectedImages = React.useMemo(() => {
      if (coverImages?.length !== 0) {
        const image = coverImages?.find(image => {
          return image?.variant_ids?.find(vId => vId === selectedVariant?.id)
        })
        return image ? [image.src] : []
      }
      return []
    }, [coverImages, selectedVariant?.id])

    const openOverlayProduct = React.useCallback(() => {
      setProductOverlay({
        isOpen: true,
        productTitle: title,
        productDescription: description,
        options: badgeHistoryData?.options ?? [],
        variants: badgeHistoryData?.variants ?? [],
        selectedImages: selectedImages,
        selectedVariant: selectedVariant,
        coverImages: coverImages,
        defaultImageSrc: coverImage,
        isProductDelete: badgeHistoryData?.isProductDelete,
        currencySymbol: badgeHistoryData?.currencySymbol,
        checkoutUrl: checkoutUrl,
        isBadgeLiveMode: false,
        action: "update",
        productImage: image,
        productCurrentPrice: currentPrice,
        productOldPrice: oldPrice,
        productButtonText: buttonText,
        productShortDescription: badgeHistoryData.shortDescription
      })
    }, [
      badgeHistoryData?.currencySymbol,
      badgeHistoryData?.isProductDelete,
      badgeHistoryData?.options,
      badgeHistoryData.shortDescription,
      badgeHistoryData?.variants,
      buttonText,
      checkoutUrl,
      coverImage,
      coverImages,
      currentPrice,
      description,
      image,
      oldPrice,
      selectedImages,
      selectedVariant,
      setProductOverlay,
      title
    ])

    const handlePiP = useCallback(
      e => {
        if (isWatchParty) {
          requestPictureInPictureWP()
        } else {
          if (mainCameraEnabled) {
            requestPictureInPicture()
          }
        }

        setPiPParams({
          isActivePiP: true,
          isRedirected: false,
          coverImage: coverImages?.[0].src
        })

        openOverlayProduct()
        e.stopPropagation()
      },

      [isWatchParty, setPiPParams, coverImages, openOverlayProduct, requestPictureInPictureWP, mainCameraEnabled, requestPictureInPicture]
    )

    const { openFullScreenProductPage } = useProductPageOverlay()

    const handlePictureInPicture = useCallback(
      async e => {
        e.stopPropagation()

        // open product page
        openFullScreenProductPage({
          url: checkoutUrl,
          showVariantScreen:
            badgeHistoryData?.variants && badgeHistoryData?.variants.length > 0,
          openOverlayProduct,
          fallbackHandler: () => {
            // Open PIP way as a fallback
            if (isShopifyProduct) {
              handlePiP(e)
            } else {
              openExternalUrl(checkoutUrl)
            }
          }
        })

        meetingClient.userEventLog?.submitProductBuyClickEvent({
          productId: id
        })

      },
      [
        id,
        meetingClient,
        openFullScreenProductPage,
        checkoutUrl,
        badgeHistoryData?.variants,
        openOverlayProduct,
        isShopifyProduct,
        handlePiP,
        openExternalUrl
      ]
    )

    const handleDeleteBadge = React.useCallback(() => {
      meetingClient?.deleteHistoryBadge(id)
    }, [id, meetingClient])

    const isSmallSizePrice = React.useMemo(() => {
      return (
        (currentPrice?.length > 6 || oldPrice?.length > 6) &&
        currentPrice.length !== 0 &&
        oldPrice.length !== 0
      )
    }, [currentPrice?.length, oldPrice?.length])

    const handleMouseEnter = React.useCallback(() => {
      setHovered(true)
    }, [])

    const handleMouseLeave = React.useCallback(() => {
      setHovered(false)
    }, [])

    const isImage = React.useMemo(() => {
      return image !== "" && image?.length > 0
    }, [image])

    const isHover = React.useMemo(() => {
      return isModerator && hovered
    }, [hovered, isModerator])

    return (
      <Container
        key={id}
        ref={badgeRef}
        roomStyles={roomStyles}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        customStyles={customStyles?.container}
      >
        {isModerator && (
          <MouseHoverButtons
            isPublished={false}
            isHovered={hovered}
            toggleOverlay={handleDeleteBadge}
            isHistoryBadge={true}
          />
        )}

        <Wrapper>
          {isImage && (
            <ImageContainer>
              <Image
                maxW="max-content"
                src={image}
                alt={title}
                maxHeight="65px"
                maxWidth="65px"
                objectFit="contain"
                opacity={isHover ? "0.2" : "1"}
              />
            </ImageContainer>
          )}
          <InfoContainer hovered={isHover} isImage={isImage}>
            <Title roomStyles={roomStyles}>{title}</Title>
            <Description roomStyles={roomStyles}>
              {cleanProductDescription(
                stripHtmlTags(
                  shortDescription && shortDescription !== ""
                    ? shortDescription
                    : description
                )
              )}
            </Description>
            <Wrapper>
              <Stack style={{ flexWrap: "wrap" }}>
                <PrimaryNumber
                  roomStyles={roomStyles}
                  isSmallSizePrice={isSmallSizePrice}
                >
                  {currentPrice}
                </PrimaryNumber>
                <SecondaryNumber
                  isSmallSizePrice={isSmallSizePrice}
                  roomStyles={roomStyles}
                >
                  {oldPrice}
                </SecondaryNumber>
              </Stack>
              <BuyButton
                textButton={buttonText}
                handleClick={handlePictureInPicture}
                customStyles={customStyles?.button}
              />
            </Wrapper>
          </InfoContainer>
        </Wrapper>
      </Container>
    )
  }
)

export default HistoryBadge
